/**
 * Created by vsuthar on 8/18/21
 * File: NoDataScreen
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

// libs
import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import {
  Button,
  Tooltip,
  Bold,
  useModal, ToastProvider,
} from '@panwds/react-ui'
import {
  PencilIcon,
  DeleteIcon,
  PlusIcon,
} from '@panwds/icons'
import { store, actions as sparkyActions } from '@sparky/framework'
import _ from 'lodash'

// reducers
import { loadCSPAccounts } from '../common/reducers/actions/LicenseReducer.actions'
import { selectTreeNode } from '../common/reducers/actions/TreeReducer.actions'

// context
import GlobalContext from '../common/context/GlobalContext'
import AuthContext from '../common/context/AuthContext'
import useReadOnly from '../common/hooks/useReadOnly'
import { useMessage } from '../common/providers/MessageProvider'
import IpRestrictProvider from '../common/context/IpRestrictContext'

// components
import RCTree from '../common/components/page/rcTree/RCTree'
import NoDataScreen from './components/noDataScreen/NoDataScreen'
import Licenses from './components/licenses/Licenses'
import FreeAndThirdPartyAppActivation from './components/licenses/FreeAndThirdPartyAppActivation'
import DeleteTSGModal from './components/licenses/Modal/DeleteTSGModal'
import Header from '../common/components/page/header/Header'
import TenantCreationModal from '../hub/components/common/TenantCreationModal'

// services
import SubscriptionService from '../subscription/services/SubscriptionService'
import { LoadLicense, LoadTreeDataAsync } from '../common/actions/GlobalActions'

// utils
import { ARRAY } from '../common/utils/Utils'
import { getFrameworkVar, getVarValue } from '../common/utils/Sparky'

// constants
import {
  BUTTON_ADD_TENANT,
  TOOLTIP_ADD_TENANT,
  TOOLTIP_DELETE_TENANT,
  TOOLTIP_EDIT_TENANT,
} from './constants/LabelConstants'
import {
  APP_ID_PRISMA_ACCESS_PANORAMA,
  TENANCY_PERMISSION_SET,
  TSG_NONE,
} from '../common/constants/GlobalConstants'
import {
  ROOT_NODE_ID,
  TENANT_ADD,
  TENANT_DELETE,
  TENANT_EDIT,
} from './constants/TenancyConstants'
import {
  FF_DEFAULT_TSG,
  FF_ENABLE_INSTANCE_LICENSE_EDIT,
  FF_ENHANCED_TSG_DELETE,
  FF_RC_TREE_V2,
} from '../common/utils/FeatureFlags'
import {
  TOOLTIP_READ_ONLY,
} from '../iam/constants/LabelConstants'
import TooltipButton from '../common/components/tooltipButton/TooltipButton'

const enhancedTSGDelete = getVarValue(FF_ENHANCED_TSG_DELETE, false)
const allowNewTenant = getVarValue(FF_ENABLE_INSTANCE_LICENSE_EDIT, false)
const enableIconMenuForAll = getVarValue(FF_RC_TREE_V2, false)
const enableDefaultTSG = getVarValue(FF_DEFAULT_TSG)

/**
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const TenancyContent = (props) => {

  const { currentView } = props
  const { apps } = props

  const activation_app_id = store.stateRef.current.commonModule?.path?.match(/\/instances\/(\w+)/)?.[1]
  const [isAddTenantScreen, setAddTenantScreen] = useState(true)
  const [actionType, setActions] = useState(undefined)
  const [popoverTenant, setPopoverTenant] = useState(null)
  const [claimInfo, setClaimInfo] = useState({})
  const [isClaimLoading, setClaimLoading] = useState(false)
  const [showActivationForm, setShowActivationForm] = useState(Boolean(activation_app_id))
  const [loadingFree3p, setLoadingFree3p] = useState(false)
  const addTSGModal = useModal(TenantCreationModal)
  const deleteTSGModal = useModal(DeleteTSGModal)
  const { createBanner } = useMessage()
  const { dispatch, treeReducer } = useContext(GlobalContext)
  const { user, auth } = useContext(AuthContext)
  const { sgKeyBy, selectedTreeNode } = treeReducer

  const readOnly = useReadOnly(selectedTreeNode?.id, TENANCY_PERMISSION_SET)

  useEffect(() => {
    dispatch(loadCSPAccounts(ARRAY.clean(user?.supportAccountIds), true))
  }, [dispatch, user?.supportAccountIds])

  useEffect(() => {
    if (currentView) {
      setActions(currentView)
    }
  }, [currentView])

  useEffect(() => {
    if (props.selectedTreeNode?.id !== ROOT_NODE_ID) {
      setPopoverTenant(props.selectedTreeNode)
    }
    setActions(undefined)
  }, [props.selectedTreeNode])

  useMemo(() => {
    if (props.globalTsgId !== ROOT_NODE_ID && props.globalTsgId !== TSG_NONE) {
      LoadLicense({
        tsg_id: props.globalTsgId,
        flow_type: 'MSP',
        license_context: 'prisma_access',
      }, dispatch)
    }
  }, [dispatch, props.globalTsgId])

  useEffect(() => {
    setClaimLoading(true)
    const claimDetails = { isSDWANPresent: false, isClaimed: false, isPaePresent: false }
    if (props.selectedTreeNode?.parent_id !== ROOT_NODE_ID) {
      claimDetails.isClaimed = false
    }
    setClaimInfo(claimDetails)
    setClaimLoading(false)
  }, [dispatch, props.selectedTreeNode?.id, props.selectedTreeNode?.parent_id])

  const free3pActivationData = useMemo(() => {
    return {
      activationAppId: activation_app_id,
      activationApp: apps[activation_app_id]
    }
  }, [activation_app_id, apps])

  const openDeleteTSGModal = useCallback(() => {
    const tenant = _.get(props, 'selectedTreeNode')
    const instances = _.get(props, `instancesKeyBy.${selectedTreeNode?.id}`, [])
    const modalProps = {
      tenant,
      instances
    }
    deleteTSGModal.openModal(modalProps)
  }, [deleteTSGModal, props, selectedTreeNode?.id])

  const onActionClick = useCallback((actionType) => {
    setPopoverTenant(props.selectedTreeNode)
    if (actionType === TENANT_DELETE) {
      openDeleteTSGModal()
    }
    setActions(actionType)
  }, [openDeleteTSGModal, props.selectedTreeNode])

  const onContextClick = useCallback((actionType, tenant) => {
    setPopoverTenant(tenant)
    if (actionType === TENANT_DELETE) {
      openDeleteTSGModal()
    }
    setActions(actionType)
  }, [openDeleteTSGModal])

  const onCancel = useCallback(() => {
    setActions(undefined)
  }, [])

  const onFree3pActivationSubmit = useCallback(async (formValues) => {
    const { support_account, region, agree } = formValues
    const tsg_id = selectedTreeNode.id
    const developer_defined_fields = {}

    if (free3pActivationData.activationApp.is_third_party) {
      free3pActivationData.activationApp?.developer_defined_fields?.forEach((field) => {
        if (formValues[field.name]) {
          developer_defined_fields[field.name] = formValues[field.name]
        }
      })
    }

    const payload = {
      app_id: free3pActivationData.activationAppId,
      support_account_id: `${support_account.id}`,
      support_account_name: support_account.description,
      region: region.id,
      is_eula_accepted: agree,
      tsg_name: selectedTreeNode?.display_name,
      developer_defined_fields
    }
    try {
      setLoadingFree3p(true)
      await SubscriptionService.instances.create({ tsg_id, request: payload })
      createBanner({ message: `Successfully activated ${free3pActivationData.activationApp.display_name}`, bannerProps: { appearance: 'success', type: 'page' } })
      setShowActivationForm(false)
      setLoadingFree3p(false)
    }
    catch (e) {
      createBanner({ message: e.message || 'An error occured during activation. Please try again.' })
      setLoadingFree3p(false)
    }
  }, [
    createBanner,
    free3pActivationData.activationAppId,
    free3pActivationData.activationApp?.display_name,
    selectedTreeNode?.display_name, selectedTreeNode?.id,
    free3pActivationData.activationApp?.developer_defined_fields,
    free3pActivationData.activationApp?.is_third_party
  ])

  const onFree3pActivationCancel = useCallback(() => {
    setShowActivationForm(false)
  }, [])

  const disableDelete = useMemo(() => {
    const id = props.selectedTreeNode?.id
    const instancesKeyBy = props.instancesKeyBy[id]
    return !!((instancesKeyBy?.length > 0 || props?.selectedTreeNode?.children?.length > 0) || !props?.selectedTreeNode || readOnly)
  }, [props.instancesKeyBy, props?.selectedTreeNode, readOnly])

  const disableAdd = useMemo(() => {
    const id = props.selectedTreeNode?.id
    const instancesKeyBy = props.instancesKeyBy[id]
    return !!((!allowNewTenant && instancesKeyBy?.some(each => each.app_id === APP_ID_PRISMA_ACCESS_PANORAMA)) || readOnly)
  }, [props.instancesKeyBy, props.selectedTreeNode?.id, readOnly])

  const disableEdit = useMemo(() => {
    return !!(!props?.selectedTreeNode || readOnly)
  }, [props?.selectedTreeNode, readOnly])

  const onCreatedTsg = useCallback((tsg) => LoadTreeDataAsync(dispatch)
    .then(() => sparkyActions.switchTsg(tsg.id))
    .then(() => dispatch(selectTreeNode(tsg))), [dispatch])

  const onAddTenantClick = useCallback(() => {
    addTSGModal.openModal({ selectedTsg: selectedTreeNode, onCreatedTsg })
  }, [addTSGModal, onCreatedTsg, selectedTreeNode])

  const onEditTenantClick = useCallback(() => {
    addTSGModal.openModal({ selectedTsg: selectedTreeNode, isEdit: true, onCreatedTsg, sgKeyBy })
  }, [addTSGModal, onCreatedTsg, selectedTreeNode, sgKeyBy])

  const actions = useMemo(() => {
    if (auth?.tsgs?.hasMsp || !enableDefaultTSG) {
      const ghostBtnStyle = { background: 'transparent' }
      if (readOnly) {
        return (
          <div className={'cm-flex cm-flex-row'}>
            <Tooltip label={TOOLTIP_READ_ONLY}>
              <div className='cm-flex cm-justify-center cm-items-center cm-grayscale cm-text-gray' style={{ height: '2rem', width: '2rem' }}>
                <DeleteIcon className='cm-text-gray-300'/>
              </div>
            </Tooltip>
            <Tooltip label={TOOLTIP_READ_ONLY}>
              <div className='cm-flex cm-justify-center cm-items-center cm-grayscale cm-text-gray' style={{ height: '2rem', width: '2rem' }}>
                <PencilIcon className='cm-text-gray-300'/>
              </div>
            </Tooltip>
            <Tooltip label={TOOLTIP_READ_ONLY}>
              <div className='cm-flex cm-justify-center cm-items-center cm-grayscale cm-text-gray' style={{ height: '2rem', width: '2rem' }}>
                <PlusIcon className='cm-text-gray-300'/>
              </div>
            </Tooltip>
          </div>
        )
      }
      return (
        <div className={'cm-flex cm-flex-row'}>
          <Tooltip label={TOOLTIP_DELETE_TENANT}>
            <Button
              disabled={!enhancedTSGDelete && disableDelete}
              onClick={onActionClick.bind(this, TENANT_DELETE)}
              aria-label={'license-trash'}
              appearance='tertiary-clear'
              style={ghostBtnStyle}
              icon={<DeleteIcon />} />
          </Tooltip>
          <Tooltip label={TOOLTIP_EDIT_TENANT}>
            <Button
              style={ghostBtnStyle}
              onClick={enableIconMenuForAll ? onEditTenantClick : onActionClick.bind(this, TENANT_EDIT)}
              aria-label={'license-edit'}
              appearance='tertiary-clear'
              disabled={disableEdit}
              icon={<PencilIcon />} />
          </Tooltip>
          <Tooltip label={TOOLTIP_ADD_TENANT}>
            <Button
              disabled={disableAdd}
              style={ghostBtnStyle}
              onClick={enableIconMenuForAll ? onAddTenantClick : onActionClick.bind(this, TENANT_ADD)}
              aria-label={'license-add'}
              appearance='tertiary-clear'
              icon={<PlusIcon />} />
          </Tooltip>
        </div>
      )
    }
    else {
      return (
        <TooltipButton
          label={TOOLTIP_ADD_TENANT}
          disabled={readOnly}
          buttonProps={{ onClick: enableIconMenuForAll ? onAddTenantClick : onActionClick.bind(this, TENANT_ADD) }}
        >
          {BUTTON_ADD_TENANT}
        </TooltipButton>
      )
    }
  }, [auth?.tsgs?.hasMsp, disableAdd, disableDelete, disableEdit, onActionClick, onAddTenantClick, onEditTenantClick, readOnly])

  const showSidebar = useMemo(() => {
    if (enableDefaultTSG) {
      return auth?.tsgs?.hasMsp
    }
    return true
  }, [auth?.tsgs?.hasMsp])

  const getChild = () => {
    const headerProps = {
      size: 'xs',
      title: <Bold>{selectedTreeNode?.display_name}</Bold>,
      level: 2,
      subTitle: selectedTreeNode?.vertical || '',
      // actions: actions,
      selectedTreeNode
    }

    if (showActivationForm && Boolean(free3pActivationData.activationApp) && getFrameworkVar('hub')) {
      return (
        <div className='cm-flex cm-flex-col cm-flex-1 cm-absolute cm-inset-0 cm-overflow-hidden'>
          <Header {...headerProps} />
          <FreeAndThirdPartyAppActivation
            app={free3pActivationData.activationApp}
            supportAccountIds={auth?.user?.supportAccountIds}
            apps={apps}
            instances={auth?.instances}
            onSubmit={onFree3pActivationSubmit}
            onCancel={onFree3pActivationCancel}
            loading={loadingFree3p}
          />
        </div>
      )
    }

    if (isAddTenantScreen) {
      const showLicenses = props.selectedTreeNode?.id !== ROOT_NODE_ID
      return (
        <div className='cm-flex cm-flex-row cm-flex-1 cm-absolute cm-inset-0 cm-overflow-hidden'>
          {showSidebar && (
            <div className="common-module-side-bar dark:cm-bg-gray-950">
              <RCTree
                {...{ claimInfo, onContextClick, ...props }}
                expandedKeys={[props?.selectedTreeNode?.parent_id]}
                showContextButton
              />
            </div>
          )}
          {showLicenses ? (
            <Licenses
              globalDispatch={props.globalDispatch}
              onCancel={onCancel}
              actionType={actionType}
              actions={actions}
              setActions={setActions}
              claimInfo={claimInfo}
              onBreadCrumbClick={props.onBreadCrumbClick}
              popoverTenant={popoverTenant}
              isClaimLoading={isClaimLoading}
              {...props}
            />
          ) : (
            <div className='cm-text-center cm-w-full cm-mt-[50%]'>
              Please select a tenant to view Licensed Products.
            </div>
          )}
        </div>
      )
    }
    return <NoDataScreen onAddTenantClick={() => setAddTenantScreen(true)} />
  }

  return getChild()
}

const Tenancy = (props) => (
  <ToastProvider>
    <IpRestrictProvider>
      <TenancyContent {...props} />
    </IpRestrictProvider>
  </ToastProvider>
)

export default Tenancy

